"use client";

import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import ArticleHero from "../ArticleHero/ArticleHero";
import { LightboxProvider } from "../LightboxProvider/LightboxProvider";
import { WPAPIPostGraphQL } from "@/types";
import { parseContent } from "../WpRenders/WpRenders";
import { useDataContext } from "@/app/_context";

const WPPostRender = ({
  index,
  post,
  hidden,
}: {
  index: number;
  post: WPAPIPostGraphQL;
  hidden?: boolean;
}) => {
  const { scrollPositionY } = useDataContext();
  const prevScrollY = useRef(scrollPositionY);
  const [scrollingDown, setScrollingDown] = useState(false);
  const navHidden = scrollingDown;

  const parsedContent = useMemo(
    () =>
      post
        ? (parseContent(post.node.content) as string | ReactNode | ReactNode[])
        : undefined,
    [post],
  );

  const readingTime = useMemo(() => {
    if (!post?.node?.content) return null;
    const wordCount = post.node.content
      .replace(/<[^>]+>/g, " ")
      .split(/\s+/)
      .filter(Boolean).length;
    return Math.ceil(wordCount / 225);
  }, [post]);

  const [views, setViews] = useState<number | null>(null);

  const hasIncrementedRef = useRef(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      if (scrollPositionY < 150) {
        setScrollingDown(false);
      } else if (scrollPositionY > prevScrollY.current) {
        setScrollingDown(true);
      } else if (scrollPositionY < prevScrollY.current) {
        setScrollingDown(false);
      }

      prevScrollY.current = scrollPositionY;
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [scrollPositionY]);

  // Views: count this reading once and show the total (see app/api/views).
  // A failure leaves the count off the page.
  useEffect(() => {
    if (hasIncrementedRef.current) return;
    hasIncrementedRef.current = true;
    fetch("/api/views", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ slug: post.node.slug }),
    })
      .then((res) => res.json())
      .then((body: { views?: unknown }) =>
        setViews(typeof body?.views === "number" ? body.views : null),
      )
      .catch(() => {});
  }, [post.node.slug]);

  if (!hidden)
    return (
      <LightboxProvider articleContent={post.node.content ?? ""}>
        <article key={index} className="post">
          <ArticleHero
            post={post}
            views={views}
            readingTime={readingTime}
            navHidden={navHidden}
          />
          <div className="content wp-content text-stone-800 dark:text-stone-300 max-w-5xl mx-auto">
            {typeof parsedContent === "string" ? (
              <div dangerouslySetInnerHTML={{ __html: parsedContent }} />
            ) : (
              parsedContent
            )}
          </div>
        </article>
      </LightboxProvider>
    );

  return null;
};

export default WPPostRender;
